import { useMemo } from "react";
import { type Application } from "@/lib/backend/api";
import {
  type UpcomingShift,
  type UpcomingShiftItem,
  useUpcomingShiftsOverview,
} from "@/lib/hooks/use-upcoming-shifts-overview";

/**
 * Where the ball is.
 *
 * - `confirmed` — you are planned in. Nothing to do but show up.
 * - `applied` — you said yes, the planner has not answered yet. Waiting on them.
 * - `invited` — they asked you, you have not answered yet. Waiting on you.
 *
 * That direction — who still owes whom an answer — is what an employee actually wants to know, and
 * it is what the old three-or-four-tab split failed to communicate: "Aangemeld", "Bevestigd" and
 * "Uitgenodigd" all read as "I sort of signed up for something".
 */
export type AgendaStatus = "confirmed" | "applied";

export type AgendaItem =
  | {
      status: "confirmed";
      key: string;
      startAt: string;
      /**
       * Zelfde type als in het overzicht, en dus zonder belofte over `shiftTimeEntry`. Hier stond
       * `Shift & { shiftTimeEntry: ShiftTimeEntry }`, overgenomen van de hook die hetzelfde
       * beweerde. Een bevestigde dienst waarvoor nog niemand uren heeft ingevoerd is de normale
       * toestand, niet de uitzondering.
       */
      shift: UpcomingShift;
    }
  | { status: "applied"; key: string; startAt: string; application: Application };

/**
 * Merges de bronnen tot één lijst op datum. Puur, zodat de ordening te testen is zonder iets te
 * renderen.
 *
 * Er was een derde bron: uitnodigingen. Die liepen via campagnes en zijn met het communicatieblok
 * verdwenen (stap 4 van `admin/docs/communicatie-verwijderen.md`).
 */
export function buildAgenda(items: UpcomingShiftItem[]): AgendaItem[] {
  const fromOverview: AgendaItem[] = items.map((item) =>
    item.type === "shift"
      ? {
          status: "confirmed" as const,
          key: `shift-${item.shift.id}`,
          startAt: item.sortKey,
          shift: item.shift,
        }
      : {
          status: "applied" as const,
          key: `application-${item.application.id}`,
          startAt: item.sortKey,
          application: item.application,
        },
  );

  return [...fromOverview].sort((a, b) => a.startAt.localeCompare(b.startAt));
}

/**
 * One chronological list of everything still ahead of you, whatever state it is in.
 *
 * Navigation splits by direction (ahead vs behind), not by status — status belongs on the card,
 * next to the thing it describes. An employee thinks in a calendar, not in a state machine: the
 * question is "what happens Saturday", not "which phase of the signup process is Saturday in".
 */
export function useUpcomingAgenda() {
  const {
    items,
    isLoading,
    loadMore,
    hasNextPage,
    refetch: refetchOverview,
  } = useUpcomingShiftsOverview();

  const agenda = useMemo(() => buildAgenda(items), [items]);

  const refetch = refetchOverview;

  return {
    agenda,
    isLoading,
    loadMore,
    hasNextPage,
    refetch,
  };
}
